import { useState } from 'react';
import { DataGridPro, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useMediaQuery, useTheme } from '@mui/material';
import { applyThemeGridSxOverrides } from '@/Infrastructure/Theme/AppThemeOverrides';
import { useFetchDocumentsApprovalQuery } from '@api/Trinium/Endpoints/Document/slice';
import { Document } from '@api/Trinium/Endpoints/Document/types';
import { GenericOverlay } from '@components/DataGrid/GenericNoRowsOverlay';
import { emptyRowSet } from '@components/DataGrid/types';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import DocumentControlGridToolbar from './GridToolbar';
import { documentControlColumnDefinitions, mobileDocumentControlColumnDefs } from './columns';

import gridStyles from '@components//DataGrid/RenderCell.module.css';
import styles from './DataGrid.DocumentControl.module.css';

/**
 * MUI Data Grid Pro component for showing Document Control for a Terminal
 */
export default function Grid() {
	const { number: terminalNumber } = useCurrentTerminal();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { data: documents } = useFetchDocumentsApprovalQuery({
		terminalNumber: terminalNumber
	});

	/**
	 * By manually tracking the state of the selected rows, this component will rerender when a row is selected which allows
	 * the toolbar to be rerendered, furthermore allowing the actions to be rerendered and enabled/disabled correctly based on
	 * the row selection model.
	 */
	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(emptyRowSet);

	return (
		<DataGridPro
			getRowId={(row: Document) => row.id}
			columns={isMobile ? mobileDocumentControlColumnDefs : documentControlColumnDefinitions}
			rowHeight={isMobile ? 200 : 48}
			className={gridStyles['custom-data-grid']}
			rows={documents ?? []}
			getRowClassName={(props) => (props.indexRelativeToCurrentPage % 2 ? styles['alternate-row'] : '')}
			checkboxSelection={true}
			isRowSelectable={() => {
				// for an unknown reason when selecting all, rowSelectionModel does not update correctly without this
				return true;
			}}
			rowSelectionModel={rowSelectionModel}
			onRowSelectionModelChange={(rowSelectionModel) => {
				setRowSelectionModel(rowSelectionModel);
			}}
			slots={{
				noRowsOverlay: () => <GenericOverlay overlayText='No Documents Require Approval.' />,
				toolbar: DocumentControlGridToolbar
			}}
			sx={{
				...applyThemeGridSxOverrides(),
				'& .MuiDataGrid-row.Mui-selected': {
					backgroundColor: 'transparent'
				},
				'& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
					backgroundColor: theme.palette.action.hover
				},
				'& .MuiDataGrid-row:hover': {
					backgroundColor: theme.palette.action.hover
				},
				'& .MuiDataGrid-cellCheckbox': {
					alignItems: 'flex-start',
					padding: 1
				}
			}}
			disableRowSelectionOnClick
		/>
	);
}
